/**
 * ░░░░░▄▀▀▀▄░░░░░░░░░░░░░░░░░
 * ▄███▀░◐░░░▌░░░░░░░░░░░░░░░░
 * ░░░░▌░░░░░▐░░░░░░░░░░░░░░░░
 * ░░░░▐░░░░░▐░░░░░░░░░░░░░░░░
 * ░░░░▌░░░░░▐▄▄░░░░░░░░░░░░░░
 * ░░░░▌░░░░▄▀▒▒▀▀▀▀▄░░░░░░░░░
 * ░░░▐░░░░▐▒▒▒▒▒▒▒▒▀▀▄░░░░░░░
 * ░░░▐░░░░▐▄▒▒▒▒▒▒▒▒▒▒▀▄░░░░░
 * ░░░░▀▄░░░░▀▄▒▒▒▒▒▒▒▒▒▒▀▄░░░
 * ░░░░░░▀▄▄▄▄▄█▄▄▄▄▄▄▄▄▄▄▄▀▄░
 * ░░░░░░░░░░░▌▌░▌▌░░░░░░░░░░░
 * ░░░░░░░░░░░▌▌░▌▌░░░░░░░░░░░
 * ░░░░░░░░░▄▄▌▌▄▌▌░░░░░░░░░░░
 *
 * MIGRATION NOTICE
 *
 * We are in the process of re-writing our components using function components
 * and React Hooks, rather than extending Component.
 *
 * We have found that hooks make our components better, and - once you get the
 * hang of them - produce fewer bugs and edge cases than conventional lifecycle
 * methods (like componentDidMount and getDerivedStateFromProps).
 *
 * However, hooks can’t be used from class components. Therefore, we are trying
 * to eliminate class components wherever we can.
 *
 * If you’re changing this file, please consider contributing to this effort by
 * changing this component to be a function that uses hooks, and then removing
 * this notice.
 */
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { css } from 'emotion';

import theme from 'site-react/theme';
import analytics from 'site-react/core/Analytics';
import DesktopNav from 'site-react/navigation/DesktopNav';
import MobileNav from 'site-react/navigation/MobileNav';
import NavigationLogo from 'site-react/shared/NavigationLogo';

import NavigationMenuButton from './components/NavigationMenuButton';
import NavigationPhoneButton from './components/NavigationPhoneButton';

const navigationStyle = css`
  align-items: center;
  background-color: ${theme.colors.white};
  border-bottom: 1px solid ${theme.colors.neutral100};
  display: flex;
  justify-content: space-between;
  height: ${theme.spacing.base * 17}px;
  left: 0;
  padding: ${theme.spacing.sm}px ${theme.spacing.md}px;
  position: sticky;
  right: 0;
  top: 0;
  z-index: ${theme.zIndex.nav};

  @media (min-width: ${theme.breakpoints.lg}px) {
    height: ${theme.spacing.base * 21}px;
  }
`;

const navigationWrapperStyle = css`
  display: flex;
  flex-grow: 1;
`;

class Navigation extends Component {
  constructor(props) {
    super(props);

    this.state = { isMobileNavOpen: false };

    this.handleCloseMobileNav = this.handleCloseMobileNav.bind(this);
    this.handleOpenMobileNav = this.handleOpenMobileNav.bind(this);
  }

  handleCloseMobileNav() {
    this.setState({ isMobileNavOpen: false });
  }

  handleOpenMobileNav() {
    analytics.track('Mobile Nav Opened', {});
    this.setState({ isMobileNavOpen: true });
  }

  render() {
    const {
      currentPage,
      isLoggedIn,
      onLogout,
      searchLocation,
      user,
    } = this.props;
    const { isMobileNavOpen } = this.state;
    const isLogoTextDisplayed = currentPage !== 'search';
    return (
      <header className={navigationStyle} role="banner">
        <div className={navigationWrapperStyle}>
          <NavigationLogo isLogoTextDisplayed={isLogoTextDisplayed} />
          {searchLocation}
        </div>
        <div>
          <NavigationPhoneButton
            analyticsMetadata={{
              location: 'Navigation Bar',
            }}
            label="Navigation Phone Button Clicked"
            phoneNumber="+44 20 3608 7359"
          />
          <NavigationMenuButton onClick={this.handleOpenMobileNav} />
          <DesktopNav
            isLoggedIn={isLoggedIn}
            logoutFunc={onLogout}
            user={user}
          />
          <MobileNav
            closeFunc={this.handleCloseMobileNav}
            isLoggedIn={isLoggedIn}
            isVisible={isMobileNavOpen}
            logoutFunc={onLogout}
          />
        </div>
      </header>
    );
  }
}

Navigation.defaultProps = {
  currentPage: '',
  searchLocation: null,
  user: null,
};

Navigation.propTypes = {
  /**
   * String representing the current page.
   */
  currentPage: PropTypes.string,

  /**
   * Is the user currently logged in? Normally comes from context.
   */
  isLoggedIn: PropTypes.bool.isRequired,

  /**
   * Search Location component. Normally comes from features/Search.
   */
  searchLocation: PropTypes.node,

  /**
   * Callback when the user asks to be logged out. Normally comes from context.
   */
  onLogout: PropTypes.func.isRequired,

  /**
   * The user object. Normally comes from context.
   */
  user: PropTypes.shape({}),
};

export default Navigation;
